import { useState, useMemo, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import GlobalNav from "../components/GlobalNav";
import ParticleCanvas from "../components/home/ParticleCanvas";
import { normalizeSlug, getAvailability } from "../utils/availability";
import { getApiUrl } from "../config/api";

const ADVISOR_TYPES = ["All", "CPA", "Payroll", "Banker", "HR", "Insurance", "Retirement"];
const CATEGORY_ACCENTS = {
  CPA: "#a8f0c6", Payroll: "#ffd97d", Banker: "#b8d4ff",
  HR: "#f9c5d1", Insurance: "#d4b8ff", Retirement: "#ffc9a0",
};
const SLUG_TO_TYPE = { cpa: "CPA", payroll: "Payroll", banker: "Banker", hr: "HR", insurance: "Insurance", retirement: "Retirement" };
const BUSINESS_SIZES = ["Just me", "2–10", "11–50", "50+"];
const BUSINESS_STAGES = [
  { label: "Startup", value: "startup" },
  { label: "Growing", value: "growing" },
  { label: "Established", value: "established" },
];

const STATE_TO_ABBREV = {
  "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR",
  "California": "CA", "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE",
  "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID",
  "Illinois": "IL", "Indiana": "IN", "Iowa": "IA", "Kansas": "KS",
  "Kentucky": "KY", "Louisiana": "LA", "Maine": "ME", "Maryland": "MD",
  "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN", "Mississippi": "MS",
  "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
  "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
  "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK",
  "Oregon": "OR", "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC",
  "South Dakota": "SD", "Tennessee": "TN", "Texas": "TX", "Utah": "UT",
  "Vermont": "VT", "Virginia": "VA", "Washington": "WA", "West Virginia": "WV",
  "Wisconsin": "WI", "Wyoming": "WY",
};
const US_STATES = Object.keys(STATE_TO_ABBREV);

const CITY_STATE_MAP = {
  "san diego": "CA", "chula vista": "CA", "el cajon": "CA", "escondido": "CA",
  "oceanside": "CA", "carlsbad": "CA", "vista": "CA", "santee": "CA",
  "la mesa": "CA", "national city": "CA", "poway": "CA", "san marcos": "CA",
  "encinitas": "CA", "lemon grove": "CA", "coronado": "CA", "imperial beach": "CA",
  "los angeles": "CA", "la": "CA", "long beach": "CA", "anaheim": "CA",
  "santa ana": "CA", "irvine": "CA", "riverside": "CA", "glendale": "CA",
  "san francisco": "CA", "sf": "CA", "oakland": "CA", "san jose": "CA",
  "sacramento": "CA", "fresno": "CA", "stockton": "CA", "bakersfield": "CA",
  "new york": "NY", "nyc": "NY", "brooklyn": "NY", "manhattan": "NY",
  "chicago": "IL", "houston": "TX", "dallas": "TX", "austin": "TX",
  "phoenix": "AZ", "tucson": "AZ", "mesa": "AZ", "scottsdale": "AZ",
  "seattle": "WA", "denver": "CO", "miami": "FL", "orlando": "FL",
  "atlanta": "GA", "boston": "MA", "portland": "OR", "las vegas": "NV",
  "minneapolis": "MN", "detroit": "MI", "nashville": "TN",
  "charlotte": "NC", "raleigh": "NC", "indianapolis": "IN",
  "milwaukee": "WI", "albuquerque": "NM", "omaha": "NE",
  "kansas city": "MO", "st. louis": "MO", "salt lake city": "UT",
  "washington": "DC", "dc": "DC", "washington dc": "DC",
};

const STATE_NAME_MAP = {
  "california": "CA", "new york": "NY", "texas": "TX", "florida": "FL",
  "illinois": "IL", "pennsylvania": "PA", "ohio": "OH", "georgia": "GA",
  "north carolina": "NC", "michigan": "MI", "new jersey": "NJ", "virginia": "VA",
  "washington": "WA", "arizona": "AZ", "massachusetts": "MA", "tennessee": "TN",
  "indiana": "IN", "missouri": "MO", "maryland": "MD", "wisconsin": "WI",
  "colorado": "CO", "minnesota": "MN", "south carolina": "SC", "alabama": "AL",
  "louisiana": "LA", "kentucky": "KY", "oregon": "OR", "oklahoma": "OK",
  "connecticut": "CT", "utah": "UT", "iowa": "IA", "nevada": "NV",
  "arkansas": "AR", "mississippi": "MS", "kansas": "KS", "new mexico": "NM",
  "nebraska": "NE", "west virginia": "WV", "idaho": "ID", "hawaii": "HI",
  "new hampshire": "NH", "maine": "ME", "montana": "MT", "rhode island": "RI",
  "delaware": "DE", "south dakota": "SD", "north dakota": "ND", "alaska": "AK",
  "vermont": "VT", "wyoming": "WY", "district of columbia": "DC",
};

const STATE_CODE_TO_NAME = Object.fromEntries(
  Object.entries(STATE_NAME_MAP).map(([name, code]) => [code, name.replace(/\b\w/g, c => c.toUpperCase())])
);


function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function resolvePhotoSrc(photoUrl) {
  if (!photoUrl) return null;
  // Full https URL (e.g. Object Storage CDN) — return as-is
  if (photoUrl.startsWith("http")) return photoUrl;
  // New Object Storage proxy path — return as-is
  if (photoUrl.startsWith("/api/photos/")) return photoUrl;
  // Legacy local-disk path: strip all /api/upload prefixes then reattach exactly one
  const stripped = photoUrl.replace(/^\/?(api\/upload\/?)+/, "");
  return `/api/upload/${stripped}`;
}

function toArr(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === "string" && v) return v.split(",").map(s => s.trim()).filter(Boolean);
  return [];
}

export function normalizeAdvisor(a) {
  const firstName = a.first_name || "";
  const lastName = a.last_name || "";
  const name = (firstName + " " + lastName).trim() || a.name || "Advisor";
  const type = SLUG_TO_TYPE[a.category_slug] || a.category_name || a.advisor_category || a.type || "Advisor";
  const accent = CATEGORY_ACCENTS[type] || "#a8f0c6";
  const availability_slug = normalizeSlug(a.availability_status);
  const rate = a.hourly_rate ? `$${a.hourly_rate}/hr` : a.pricing_type_name || a.pricing || "Contact";
  const tags = toArr(a.specialties);
  const industries = toArr(a.industries).length ? toArr(a.industries) : toArr(a.industries_served);
  return {
    id: a.id,
    name,
    type,
    years: a.years_experience || 0,
    rating: parseFloat(a.rating) || 0,
    reviews: a.review_count || 0,
    rate,
    location: a.location || (a.remote_available ? "Remote" : ""),
    remote: !!a.remote_available,
    availability_slug,
    headline: a.headline || "",
    bio: a.bio || a.headline || "",
    tags: tags.slice(0, 3),
    allTags: tags,
    industries: industries.slice(0, 4),
    businessSizes: toArr(a.business_sizes),
    isVerified: true,
    isNew: a.billing_activated_at ? (Date.now() - new Date(a.billing_activated_at).getTime()) < 30 * 24 * 60 * 60 * 1000 : false,
    freeConsult: !!a.free_consultation,
    accent,
    photoUrl: a.profile_photo_url || a.profile_photo || null,
    licensed_states: a.licensed_states || null,
    profile_slug: a.profile_slug || null,
    booking_link: a.booking_link || a.calendly_url || null,
    free_consultation: !!a.free_consultation,
    challenges: toArr(a.client_challenges),
    hobbies: toArr(a.hobbies),
    goodFitFor: toArr(a.good_fit_for),
    stages: toArr(a.business_stages),
    whyIDoThis: a.why_i_do_this || null,
  };
}

function AvailabilityBadge({ slug }) {
  const v = getAvailability(slug);
  return (
    <span style={{ fontSize: 11, fontFamily: "sans-serif", fontWeight: 700, color: v.color, background: v.bg, padding: "3px 9px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: v.color, display: "inline-block" }} />
      {v.label}
    </span>
  );
}


function FilterTag({ label, onRemove }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px 4px 12px", borderRadius: 999, background: "#004D35", color: "#fff", fontSize: 12, fontFamily: "sans-serif", fontWeight: 600 }}>
      {label}
      <button onClick={onRemove} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.8)", cursor: "pointer", padding: "0 2px", fontSize: 15, lineHeight: 1, display: "flex", alignItems: "center" }}>×</button>
    </span>
  );
}

function FilterPanel({ activeType: initialType, activeSize, stateFilter, availabilityFilter, remoteFilter, stageFilter: initialStage, onApply, onClose, isMobile }) {
  const [type, setType] = useState(initialType || "All");
  const [size, setSize] = useState(activeSize);
  const [state, setState] = useState(stateFilter);
  const [avail, setAvail] = useState(availabilityFilter);
  const [remote, setRemote] = useState(remoteFilter);
  const [stage, setStage] = useState(initialStage || "");

  const clearAll = () => { setType("All"); setSize(""); setState(""); setAvail(""); setRemote(""); setStage(""); };
  const handleApply = () => { onApply({ type, size, state, avail, remote, stage }); onClose(); };
  const count = (type !== "All" ? 1 : 0) + (size ? 1 : 0) + (state ? 1 : 0) + (avail ? 1 : 0) + (remote ? 1 : 0) + (stage ? 1 : 0);

  const pill = (label, active, onClick) => (
    <button key={label} onClick={onClick} style={{ padding: "7px 14px", borderRadius: 999, border: "1.5px solid", borderColor: active ? "#004D35" : "#e0ddd8", background: active ? "#004D35" : "#fff", color: active ? "#fff" : "#444", fontSize: 13, fontFamily: "sans-serif", fontWeight: active ? 600 : 400, cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap" }}>
      {label}
    </button>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 24 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(10,26,15,0.45)", backdropFilter: "blur(4px)" }} />
      <div style={{ position: "relative", background: "#fff", zIndex: 1, width: "100%", maxWidth: isMobile ? "100%" : 480, borderRadius: isMobile ? "20px 20px 0 0" : 20, boxShadow: "0 24px 60px rgba(0,0,0,0.18)", maxHeight: isMobile ? "90vh" : "80vh", display: "flex", flexDirection: "column" }}>
        {isMobile && <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}><div style={{ width: 36, height: 4, borderRadius: 999, background: "#e0ddd8" }} /></div>}
        <div style={{ padding: "18px 24px 14px", borderBottom: "1px solid #f0ede8", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <h2 style={{ fontSize: 17, fontWeight: 700, color: "#0a1a0f", margin: 0, fontFamily: "Georgia, serif" }}>Filters {count > 0 && <span style={{ fontSize: 13, fontWeight: 700, color: "#004D35", background: "#a8f0c6", padding: "1px 8px", borderRadius: 999, marginLeft: 6 }}>{count}</span>}</h2>
          <button onClick={onClose} style={{ background: "#f5f3ef", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 16, color: "#888", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>

        <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, display: "flex", flexDirection: "column", gap: 22 }}>
          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Advisor Type</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {ADVISOR_TYPES.map(t => pill(t, type === t, () => setType(t)))}
            </div>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Team Size</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {BUSINESS_SIZES.map(s => pill(s, size === s, () => setSize(size === s ? "" : s)))}
            </div>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>State</p>
            <select value={state} onChange={e => setState(e.target.value)} style={{ padding: "8px 12px", borderRadius: 9, border: `1.5px solid ${state ? "#004D35" : "#e0ddd8"}`, fontSize: 13, fontFamily: "sans-serif", color: state ? "#004D35" : "#555", background: state ? "rgba(0,77,53,0.04)" : "#fff", outline: "none", cursor: "pointer", width: "100%", fontWeight: state ? 600 : 400 }}>
              <option value="">All states</option>
              {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Availability</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {pill("Available Now", avail === "available_now", () => setAvail(avail === "available_now" ? "" : "available_now"))}
              {pill("This Week", avail === "available_this_week", () => setAvail(avail === "available_this_week" ? "" : "available_this_week"))}
              {pill("Limited", avail === "limited", () => setAvail(avail === "limited" ? "" : "limited"))}
            </div>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Remote</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {pill("Remote Friendly", remote === "remote_friendly", () => setRemote(remote === "remote_friendly" ? "" : "remote_friendly"))}
              {pill("Local Only", remote === "local_only", () => setRemote(remote === "local_only" ? "" : "local_only"))}
              {pill("Either", remote === "either", () => setRemote(remote === "either" ? "" : "either"))}
            </div>
          </div>

          <div>
            <p style={{ fontSize: 12, fontWeight: 700, color: "#888", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 10px" }}>Business Stage</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
              {BUSINESS_STAGES.map(s => pill(s.label, stage === s.value, () => setStage(stage === s.value ? "" : s.value)))}
            </div>
          </div>

        </div>

        <div style={{ padding: "14px 24px 20px", borderTop: "1px solid #f0ede8", display: "flex", gap: 10, flexShrink: 0 }}>
          {count > 0 && <button onClick={clearAll} style={{ flex: "0 0 auto", background: "#f5f3ef", border: "none", borderRadius: 999, padding: "12px 18px", fontSize: 13, fontFamily: "sans-serif", color: "#888", cursor: "pointer", fontWeight: 500 }}>Clear all</button>}
          <button onClick={handleApply} style={{ flex: 1, background: "#004D35", color: "#fff", border: "none", padding: "13px 20px", borderRadius: 999, fontSize: 14, fontFamily: "sans-serif", fontWeight: 700, cursor: "pointer" }}>Show results →</button>
        </div>
      </div>
    </div>
  );
}

export function AdvisorProfileModal({ advisor, allAdvisors, onClose, isMobile }) {
  const [mode, setMode] = useState("single");
  const [compareWith, setCompareWith] = useState(null);
  const [compareSearch, setCompareSearch] = useState("");
  const [compareCategory, setCompareCategory] = useState(advisor.type);
  const [showCatPicker, setShowCatPicker] = useState(false);

  const sameCategory = allAdvisors.filter(a => a.id !== advisor.id && a.type === compareCategory);
  const filteredCompare = compareSearch
    ? sameCategory.filter(a => a.name.toLowerCase().includes(compareSearch.toLowerCase()))
    : sameCategory;

  const isWide = mode === "comparing" && !isMobile;
  const modalW = isWide ? 860 : 580;

  const AdvisorColumn = ({ adv, isOriginal }) => {
    const photo = resolvePhotoSrc(adv.photoUrl);
    const initials = adv.name.split(" ").map(n => n[0]).join("").toUpperCase();
    return (
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {photo ? (
            <img src={photo} alt={adv.name} style={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: adv.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 700, color: "#004D35", fontFamily: "Georgia, serif", flexShrink: 0 }}>{initials}</div>
          )}
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{adv.name}</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
              <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "rgba(0,77,53,0.08)", padding: "2px 7px", borderRadius: 999, textTransform: "uppercase" }}>{adv.type}</span>
              <AvailabilityBadge slug={adv.availability_slug} />
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, fontSize: 13, fontFamily: "sans-serif", color: "#555" }}>
          {adv.rate !== "Contact" && <span style={{ fontWeight: 700, color: "#0a1a0f" }}>{adv.rate}</span>}
          {adv.years > 0 && <span>{adv.years} yrs exp</span>}
        </div>
        <div style={{ marginTop: 4 }}>
          <AdvisorLocationBlock advisor={adv} fontSize={13} />
        </div>

        {adv.allTags.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>Specialties</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {adv.allTags.slice(0, 5).map(t => <span key={t} style={{ fontSize: 11, fontFamily: "sans-serif", color: "#666", background: "#f5f3ef", padding: "3px 9px", borderRadius: 999 }}>{t}</span>)}
            </div>
          </div>
        )}

        {adv.industries.length > 0 && (
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 6px" }}>Industries</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {adv.industries.map(i => <span key={i} style={{ fontSize: 11, fontFamily: "sans-serif", color: "#666", background: "#f5f3ef", padding: "3px 9px", borderRadius: 999 }}>{i}</span>)}
            </div>
          </div>
        )}

        <div style={{ borderTop: "1px solid #f0ede8", paddingTop: 14 }}>
          <Link to={`/advisor/${adv.profile_slug || adv.id}`} style={{ display: "block", textAlign: "center", fontSize: 13, fontFamily: "sans-serif", color: "#004D35", fontWeight: 600, textDecoration: "none" }}>
            View full profile →
          </Link>
          {!isOriginal && (
            <button onClick={() => { setCompareWith(null); setMode("single"); }} style={{ display: "block", width: "100%", marginTop: 8, fontSize: 12, fontFamily: "sans-serif", color: "#bbb", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}>
              ✕ Remove
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center", padding: isMobile ? 0 : 20 }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(10,26,15,0.55)", backdropFilter: "blur(6px)" }} />
      <div style={{ position: "relative", background: "#fff", zIndex: 1, width: "100%", maxWidth: isMobile ? "100%" : modalW, borderRadius: isMobile ? "20px 20px 0 0" : 20, boxShadow: "0 32px 80px rgba(0,0,0,0.22)", maxHeight: isMobile ? "95vh" : "88vh", display: "flex", flexDirection: "column", transition: "max-width 0.3s ease" }}>
        {isMobile && <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 2px" }}><div style={{ width: 36, height: 4, borderRadius: 999, background: "#e0ddd8" }} /></div>}

        <div style={{ padding: "14px 20px 12px", borderBottom: "1px solid #f0ede8", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          {mode !== "single" ? (
            <button onClick={() => { if (mode === "picking") setMode("single"); else { setCompareWith(null); setMode("single"); } }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 13, fontFamily: "sans-serif", color: "#004D35", fontWeight: 600, padding: 0, display: "flex", alignItems: "center", gap: 4 }}>
              ← {mode === "picking" ? "Back" : "Start over"}
            </button>
          ) : <div />}
          <button onClick={onClose} style={{ background: "#f5f3ef", border: "none", borderRadius: "50%", width: 30, height: 30, cursor: "pointer", fontSize: 16, color: "#888", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: isMobile ? "20px 20px 28px" : "24px 28px 28px" }}>

          {mode === "single" && (() => {
            const photo = resolvePhotoSrc(advisor.photoUrl);
            const initials = advisor.name.split(" ").map(n => n[0]).join("").toUpperCase();
            return (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
                  {photo ? (
                    <img src={photo} alt={advisor.name} style={{ width: 72, height: 72, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 72, height: 72, borderRadius: "50%", background: advisor.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontWeight: 700, color: "#004D35", fontFamily: "Georgia, serif", flexShrink: 0 }}>{initials}</div>
                  )}
                  <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: "#0a1a0f", margin: "0 0 6px", fontFamily: "Georgia, serif" }}>{advisor.name}</h2>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "rgba(0,77,53,0.08)", padding: "2px 8px", borderRadius: 999, textTransform: "uppercase" }}>{advisor.type}</span>
                      <AvailabilityBadge slug={advisor.availability_slug} />
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 6, fontSize: 13, fontFamily: "sans-serif", color: "#666" }}>
                      {advisor.rate !== "Contact" && <span style={{ fontWeight: 700, color: "#0a1a0f" }}>{advisor.rate}</span>}
                      {advisor.years > 0 && <span>{advisor.years} yrs exp</span>}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <AdvisorLocationBlock advisor={advisor} fontSize={13} />
                    </div>
                  </div>
                </div>

                {(advisor.headline || advisor.bio) && (
                  <div style={{ marginBottom: 16 }}>
                    {advisor.headline && <p style={{ fontSize: 14, fontStyle: "italic", color: "#555", fontFamily: "Georgia, serif", margin: "0 0 6px", lineHeight: 1.55 }}>{advisor.headline}</p>}
                    {advisor.bio && advisor.bio !== advisor.headline && <p style={{ fontSize: 13, color: "#777", fontFamily: "sans-serif", margin: 0, lineHeight: 1.6 }}>{advisor.bio.length > 220 ? advisor.bio.slice(0, 220) + "…" : advisor.bio}</p>}
                  </div>
                )}

                {advisor.allTags.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 7px" }}>Specialties</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {advisor.allTags.slice(0, 6).map(t => <span key={t} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#555", background: "#f5f3ef", padding: "4px 11px", borderRadius: 999 }}>{t}</span>)}
                    </div>
                  </div>
                )}

                {advisor.industries.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 7px" }}>Industries</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {advisor.industries.map(i => <span key={i} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#555", background: "#f5f3ef", padding: "4px 11px", borderRadius: 999 }}>{i}</span>)}
                    </div>
                  </div>
                )}

                {advisor.goodFitFor.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 7px" }}>Who I work with</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {advisor.goodFitFor.map(g => <span key={g} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#555", background: "#f5f3ef", padding: "4px 11px", borderRadius: 999 }}>{g}</span>)}
                    </div>
                  </div>
                )}

                {advisor.challenges.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 7px" }}>Challenges I help solve</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {advisor.challenges.map(c => <span key={c} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#555", background: "#f5f3ef", padding: "4px 11px", borderRadius: 999 }}>{c}</span>)}
                    </div>
                  </div>
                )}

                {advisor.hobbies.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <p style={{ fontSize: 11, fontWeight: 700, color: "#aaa", fontFamily: "sans-serif", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 7px" }}>Outside work</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {advisor.hobbies.map(h => <span key={h} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#555", background: "#f5f3ef", padding: "4px 11px", borderRadius: 999 }}>{h}</span>)}
                    </div>
                  </div>
                )}


                <div style={{ borderTop: "1px solid #f0ede8", paddingTop: 16, marginBottom: 16 }}>
                  <Link to={`/advisor/${advisor.profile_slug || advisor.id}`} style={{ display: "block", textAlign: "center", fontSize: 13, fontFamily: "sans-serif", color: "#004D35", fontWeight: 600, textDecoration: "none" }}>
                    View full profile →
                  </Link>
                </div>

                <div style={{ borderTop: "1px solid #f0ede8", paddingTop: 14 }}>
                  <button onClick={() => setMode("picking")} style={{ width: "100%", background: "#f5f3ef", border: "1.5px dashed #ccc", borderRadius: 12, padding: "12px 16px", fontSize: 13, fontFamily: "sans-serif", color: "#555", cursor: "pointer", fontWeight: 600, textAlign: "center" }}>
                    + Compare with another advisor
                  </button>
                </div>
              </>
            );
          })()}

          {mode === "picking" && (
            <>
              <div style={{ marginBottom: 16 }}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 4px" }}>
                  Compare other {compareCategory} advisors
                </h3>
                <button onClick={() => setShowCatPicker(!showCatPicker)} style={{ fontSize: 12, fontFamily: "sans-serif", color: "#004D35", background: "none", border: "none", cursor: "pointer", padding: 0, fontWeight: 600, textDecoration: "underline" }}>
                  {showCatPicker ? "Hide categories" : "Change category"}
                </button>
              </div>

              {showCatPicker && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
                  {ADVISOR_TYPES.filter(t => t !== "All").map(t => (
                    <button key={t} onClick={() => { setCompareCategory(t); setShowCatPicker(false); setCompareSearch(""); }} style={{ padding: "5px 12px", borderRadius: 999, border: "1.5px solid", borderColor: compareCategory === t ? "#004D35" : "#e0ddd8", background: compareCategory === t ? "#004D35" : "#fff", color: compareCategory === t ? "#fff" : "#444", fontSize: 12, fontFamily: "sans-serif", fontWeight: 600, cursor: "pointer" }}>
                      {t}
                    </button>
                  ))}
                </div>
              )}

              <div style={{ position: "relative", marginBottom: 14 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#ccc", fontSize: 14 }}>⌕</span>
                <input value={compareSearch} onChange={e => setCompareSearch(e.target.value)} placeholder="Search advisors..." style={{ width: "100%", padding: "9px 12px 9px 30px", border: "1.5px solid #e0ddd8", borderRadius: 9, fontSize: 13, fontFamily: "sans-serif", color: "#333", outline: "none", boxSizing: "border-box" }} onFocus={e => e.target.style.borderColor = "#004D35"} onBlur={e => e.target.style.borderColor = "#e0ddd8"} />
              </div>

              {filteredCompare.length === 0 ? (
                <p style={{ fontSize: 13, color: "#aaa", fontFamily: "sans-serif", textAlign: "center", padding: "24px 0" }}>No other {compareCategory} advisors found.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {filteredCompare.map(a => {
                    const photo = resolvePhotoSrc(a.photoUrl);
                    const initials = a.name.split(" ").map(n => n[0]).join("").toUpperCase();
                    return (
                      <button key={a.id} onClick={() => { setCompareWith(a); setMode("comparing"); }} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 10px", background: "none", border: "none", borderBottom: "1px solid #f5f3ef", cursor: "pointer", textAlign: "left", borderRadius: 8, transition: "background 0.1s" }} onMouseEnter={e => e.currentTarget.style.background = "#f9f8f6"} onMouseLeave={e => e.currentTarget.style.background = "none"}>
                        {photo ? <img src={photo} alt={a.name} style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} /> : <div style={{ width: 40, height: 40, borderRadius: "50%", background: a.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, color: "#004D35", fontFamily: "Georgia, serif", flexShrink: 0 }}>{initials}</div>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{a.name}</div>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                            <AvailabilityBadge slug={a.availability_slug} />
                            <span style={{ fontSize: 12, color: "#888", fontFamily: "sans-serif" }}>{a.rate}</span>
                          </div>
                        </div>
                        <span style={{ fontSize: 18, color: "#ccc" }}>›</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {mode === "comparing" && compareWith && (
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 28 : 32 }}>
              <AdvisorColumn adv={advisor} isOriginal={true} />
              {!isMobile && <div style={{ width: 1, background: "#f0ede8", flexShrink: 0 }} />}
              {isMobile && <div style={{ height: 1, background: "#f0ede8" }} />}
              <AdvisorColumn adv={compareWith} isOriginal={false} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AdvisorLocationBlock({ advisor, fontSize = 12 }) {
  const isCpaOrIns = advisor.type === "CPA" || advisor.type === "Insurance";
  const states = advisor.licensed_states;
  const hasStates = states && states.length > 0;
  const stateText = hasStates
    ? (states.includes("all") ? "Licensed in all states" : `Licensed in: ${states.map(s => STATE_TO_ABBREV[s] || s).join(", ")}`)
    : null;
  return (
    <div>
      {advisor.location && <div style={{ fontSize, color: "#999", fontFamily: "sans-serif" }}>📍 {advisor.location}</div>}
      {isCpaOrIns && stateText && <div style={{ fontSize, color: "#888", fontFamily: "sans-serif" }}>{stateText}</div>}
    </div>
  );
}

function AdvisorBadges({ advisor }) {
  return (
    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
      {advisor.isVerified && (
        <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "rgba(0,77,53,0.1)", padding: "2px 8px", borderRadius: 999, display: "inline-flex", alignItems: "center", gap: 3 }}>
          <span style={{ fontSize: 9 }}>✓</span> Verified
        </span>
      )}
      {advisor.isNew && (
        <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#0369a1", background: "#e0f2fe", padding: "2px 8px", borderRadius: 999 }}>
          New
        </span>
      )}
      {advisor.freeConsult && (
        <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#92400e", background: "#fef3c7", padding: "2px 8px", borderRadius: 999 }}>
          Free Consult
        </span>
      )}
    </div>
  );
}

function AdvisorCard({ advisor, view, isMobile, onOpenModal, isBestMatch }) {
  const [hovered, setHovered] = useState(false);
  const initials = advisor.name.split(" ").map(n => n[0]).join("").toUpperCase();
  const photo = resolvePhotoSrc(advisor.photoUrl);

  const handleCardClick = (e) => {
    if (e.defaultPrevented) return;
    onOpenModal(advisor);
  };

  if (isMobile || view === "grid") {
    return (
      <div onClick={handleCardClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ background: "#fff", border: `1.5px solid ${hovered ? "#004D35" : "#e8e5e0"}`, borderRadius: 16, padding: isMobile ? "20px" : "24px", display: "flex", flexDirection: "column", transition: "all 0.2s ease", boxShadow: hovered ? "0 4px 20px rgba(0,77,53,0.1)" : "0 1px 4px rgba(0,0,0,0.04)", position: "relative", cursor: "pointer" }}>
        <div style={{ position: "absolute", top: 12, right: 12 }}><AdvisorBadges advisor={advisor} /></div>
        {isBestMatch && <span style={{ position: "absolute", top: 12, left: 12, fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "#a8f0c6", padding: "3px 9px", borderRadius: 999 }}>◆ BEST MATCH</span>}

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          {photo ? (
            <img src={photo} alt={advisor.name} style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
          ) : (
            <div style={{ width: 44, height: 44, borderRadius: "50%", background: advisor.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 700, color: "#004D35", fontFamily: "Georgia, serif", flexShrink: 0 }}>{initials}</div>
          )}
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{advisor.name}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 2 }}>
              <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "rgba(0,77,53,0.08)", padding: "1px 7px", borderRadius: 999, textTransform: "uppercase" }}>{advisor.type}</span>
              {advisor.years > 0 && <span style={{ fontSize: 11, color: "#bbb", fontFamily: "sans-serif" }}>{advisor.years} yrs</span>}
            </div>
          </div>
        </div>


        {advisor.headline && <p style={{ fontSize: 13, color: "#666", margin: "0 0 10px", fontFamily: "sans-serif", lineHeight: 1.55 }}>{advisor.headline}</p>}

        {advisor.tags.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 8 }}>
            {advisor.tags.slice(0, 2).map(t => <span key={t} style={{ fontSize: 11, fontFamily: "sans-serif", color: "#666", background: "#f5f3ef", padding: "2px 9px", borderRadius: 999 }}>{t}</span>)}
          </div>
        )}

        {advisor.stages && advisor.stages.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
            {advisor.stages.map(s => {
              const lbl = BUSINESS_STAGES.find(b => b.value === s)?.label || s;
              return <span key={s} style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 600, color: "#5b21b6", background: "#ede9fe", padding: "2px 8px", borderRadius: 999 }}>{lbl}</span>;
            })}
          </div>
        )}

        <div style={{ flex: 1 }} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: 12, borderTop: "1px solid #f0ede8", marginBottom: 12 }}>
          <div>
            {advisor.rate !== "Contact" && <div style={{ fontSize: 15, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{advisor.rate}</div>}
            <AdvisorLocationBlock advisor={advisor} fontSize={12} />
          </div>
          <AvailabilityBadge slug={advisor.availability_slug} />
        </div>

        {advisor.booking_link && (
          <div style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "rgba(13,107,75,0.08)", color: "#004D35", borderRadius: 999, padding: "5px 12px", fontSize: 12, fontFamily: "sans-serif", fontWeight: 600 }}>
            📅 Accepts bookings
          </div>
        )}
      </div>
    );
  }

  return (
    <div onClick={handleCardClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} style={{ background: "#fff", border: `1.5px solid ${hovered ? "#004D35" : "#e8e5e0"}`, borderRadius: 16, padding: "22px 28px", display: "flex", alignItems: "center", gap: 20, transition: "all 0.2s ease", boxShadow: hovered ? "0 4px 20px rgba(0,77,53,0.08)" : "none", cursor: "pointer" }}>
      {photo ? (
        <img src={photo} alt={advisor.name} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
      ) : (
        <div style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: advisor.accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 700, color: "#004D35", fontFamily: "Georgia, serif" }}>{initials}</div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3, flexWrap: "wrap" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{advisor.name}</span>
          <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "rgba(0,77,53,0.08)", padding: "2px 7px", borderRadius: 999, textTransform: "uppercase" }}>{advisor.type}</span>
          <AdvisorBadges advisor={advisor} />
          {isBestMatch && <span style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 700, color: "#004D35", background: "#a8f0c6", padding: "3px 9px", borderRadius: 999 }}>◆ BEST MATCH</span>}
        </div>
        {advisor.years > 0 && <div style={{ fontSize: 12, color: "#999", fontFamily: "sans-serif", marginBottom: 5 }}>{advisor.years} yrs exp</div>}
        {advisor.headline && <p style={{ fontSize: 13, color: "#666", margin: "0 0 7px", fontFamily: "sans-serif", lineHeight: 1.5 }}>{advisor.headline}</p>}
        {advisor.tags.length > 0 && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: advisor.stages && advisor.stages.length > 0 ? 5 : 0 }}>{advisor.tags.map(t => <span key={t} style={{ fontSize: 11, fontFamily: "sans-serif", color: "#666", background: "#f5f3ef", padding: "2px 9px", borderRadius: 999 }}>{t}</span>)}</div>}
        {advisor.stages && advisor.stages.length > 0 && (
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 4 }}>
            {advisor.stages.map(s => {
              const lbl = BUSINESS_STAGES.find(b => b.value === s)?.label || s;
              return <span key={s} style={{ fontSize: 10, fontFamily: "sans-serif", fontWeight: 600, color: "#5b21b6", background: "#ede9fe", padding: "2px 8px", borderRadius: 999 }}>{lbl}</span>;
            })}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
        <div style={{ textAlign: "right" }}>
          {advisor.rate !== "Contact" && <div style={{ fontSize: 16, fontWeight: 700, color: "#0a1a0f", fontFamily: "Georgia, serif" }}>{advisor.rate}</div>}
          <AdvisorLocationBlock advisor={advisor} fontSize={12} />
        </div>
        <AvailabilityBadge slug={advisor.availability_slug} />
      </div>
    </div>
  );
}

export default function AdvisorBrowse({ embedded = false, initialCategory = null }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  const getUrlParams = useCallback(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      category: p.get("category") || "",
      clientSize: p.get("clientSize") || "",
      remote: p.get("remote") || "",
      availability: p.get("availability") || "",
      state: p.get("state") || "",
      stage: p.get("stage") || "",
      q: p.get("q") || "",
    };
  }, []);

  const initParams = getUrlParams();

  const [allAdvisors, setAllAdvisors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [profileModal, setProfileModal] = useState(null);
  const [activeType, setActiveType] = useState(() => {
    if (initialCategory) return SLUG_TO_TYPE[initialCategory] || "All";
    return SLUG_TO_TYPE[initParams.category] || "All";
  });
  const [activeSize, setActiveSize] = useState(initParams.clientSize);
  const [stateFilter, setStateFilter] = useState(initParams.state);
  const [availabilityFilter, setAvailabilityFilter] = useState(initParams.availability);
  const [remoteFilter, setRemoteFilter] = useState(initParams.remote);
  const [stageFilter, setStageFilter] = useState(initParams.stage);
  const [view, setView] = useState("grid");
  const [sort, setSort] = useState("available");
  const [search, setSearch] = useState(initParams.q);
  const [debouncedSearch, setDebouncedSearch] = useState(initParams.q);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(null);
  const [pages, setPages] = useState(1);
  const [searchError, setSearchError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  const [showBanner, setShowBanner] = useState(() => new URLSearchParams(window.location.search).toString() !== '');
  const [arrivalSummary] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const parts = [];
    const catMap = { cpa: 'CPA advisors', payroll: 'Payroll specialists', banker: 'Business bankers', hr: 'HR professionals', insurance: 'Insurance advisors', retirement: 'Retirement planners' };
    const sizeMap = { 'Just me': 'solo business', '2\u201310': '2\u201310 person team', '11\u201350': '11\u201350 person company', '50+': '50+ company' };
    const cat = p.get('category'); const size = p.get('clientSize');
    const stage = p.get('stage'); const remote = p.get('remote');
    if (cat && catMap[cat]) parts.push(catMap[cat]);
    if (size && sizeMap[size]) parts.push(sizeMap[size]);
    if (stage) parts.push(stage.charAt(0).toUpperCase() + stage.slice(1) + ' stage');
    if (remote === 'remote_friendly') parts.push('remote-friendly');
    return parts.join(' \u00b7 ') || 'your business type';
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let active = true;
    const fetchAdvisors = async () => {
      setLoading(true);
      setSearchError('');
      try {
        const params = new URLSearchParams({ limit: '24', page: String(page) });
        if (debouncedSearch) params.set('q', debouncedSearch);
        const typeSlug = Object.entries(SLUG_TO_TYPE).find(([, value]) => value === activeType)?.[0];
        if (typeSlug && activeType !== 'All') params.set('category', typeSlug);
        if (stateFilter) params.set('state', stateFilter);
        if (activeSize) params.set('businessSize', activeSize === 'Just me' ? 'solo' : activeSize.replace(/[–—]/g, '-'));
        if (availabilityFilter) params.set('availability', availabilityFilter);
        if (remoteFilter && remoteFilter !== 'either') params.set('remote', remoteFilter);
        if (stageFilter) params.set('stage', stageFilter);
        const res = await fetch(`/api/advisors/search?${params.toString()}`);
        if (res.ok) {
          const data = await res.json();
          if (!active) return;
          setAllAdvisors((data.advisors || []).map(normalizeAdvisor));
          const total = Number(data.total ?? data.pagination?.total ?? data.totalCount);
          setTotalCount(Number.isFinite(total) ? total : null);
          const pagesCount = Number(data.totalPages ?? data.pagination?.totalPages ?? data.pages ?? (Number.isFinite(total) ? Math.ceil(total / 24) : data.hasMore === true ? page + 1 : page));
          setPages(Number.isFinite(pagesCount) && pagesCount > 0 ? pagesCount : 1);
        } else {
          const body = await res.json().catch(() => ({}));
          if (!active) return;
          setSearchError(body.error || 'Unable to load advisors right now.');
        }
      } catch (e) {
        console.error("Failed to fetch advisors:", e);
        if (active) setSearchError('Unable to load advisors right now. Check your connection and try again.');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchAdvisors();
    return () => { active = false; };
  }, [debouncedSearch, page, activeType, stateFilter, activeSize, availabilityFilter, remoteFilter, stageFilter, retryCount]);

  useEffect(() => { setPage(1); }, [debouncedSearch, activeType, stateFilter, activeSize, availabilityFilter, remoteFilter, stageFilter]);

  useEffect(() => {
    if (embedded) return;
    const params = new URLSearchParams();
    const typeSlug = Object.entries(SLUG_TO_TYPE).find(([, v]) => v === activeType)?.[0];
    if (typeSlug && activeType !== "All") params.set("category", typeSlug);
    if (activeSize) params.set("clientSize", activeSize);
    if (remoteFilter && remoteFilter !== "either") params.set("remote", remoteFilter);
    if (availabilityFilter) params.set("availability", availabilityFilter);
    if (stateFilter) params.set("state", stateFilter);
    if (stageFilter) params.set("stage", stageFilter);
    if (debouncedSearch) params.set("q", debouncedSearch);
    const newSearch = params.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (newSearch !== currentSearch) {
      navigate({ search: newSearch ? `?${newSearch}` : "" }, { replace: true });
    }
  }, [activeType, activeSize, remoteFilter, availabilityFilter, stateFilter, stageFilter, debouncedSearch, embedded]);

  const filterCount = (activeSize ? 1 : 0) + (stateFilter ? 1 : 0) + (availabilityFilter ? 1 : 0) + (remoteFilter && remoteFilter !== "either" ? 1 : 0) + (stageFilter ? 1 : 0);
  const hasFilters = activeType !== "All" || search || filterCount > 0;

  const clearAll = () => {
    setActiveType("All");
    setActiveSize("");
    setStateFilter("");
    setAvailabilityFilter("");
    setRemoteFilter("");
    setStageFilter("");
    setSearch("");
    setSort("available");
    setShowBanner(false);
  };

  const handleFilterApply = ({ type, size, state, avail, remote, stage }) => {
    if (type !== undefined) setActiveType(type);
    setActiveSize(size || "");
    setStateFilter(state || "");
    setAvailabilityFilter(avail || "");
    setRemoteFilter(remote || "");
    setStageFilter(stage || "");
  };

  const applySort = (list) => {
    if (sort === "available") return [...list].sort((a, b) => ({ available_now: 0, available_this_week: 1, limited: 2 }[a.availability_slug] ?? 3) - ({ available_now: 0, available_this_week: 1, limited: 2 }[b.availability_slug] ?? 3));
    if (sort === "reviews") return [...list].sort((a, b) => b.reviews - a.reviews);
    return list;
  };

  const filterResult = useMemo(() => {
    return { items: applySort(allAdvisors), relaxedMessage: null };
  }, [allAdvisors, sort]);

  return (
    <div style={{ background: "#f5f3ef", fontFamily: "Georgia, serif", ...(embedded ? {} : { minHeight: "100vh" }) }}>
      {showFilterPanel && <FilterPanel activeType={activeType} activeSize={activeSize} stateFilter={stateFilter} availabilityFilter={availabilityFilter} remoteFilter={remoteFilter} stageFilter={stageFilter} onApply={handleFilterApply} onClose={() => setShowFilterPanel(false)} isMobile={isMobile} />}
      {profileModal && <AdvisorProfileModal advisor={profileModal} allAdvisors={allAdvisors} onClose={() => setProfileModal(null)} isMobile={isMobile} />}

      {!embedded && <GlobalNav page="browse" />}

      {!embedded && (
        showBanner ? (
          <div style={{ background: "linear-gradient(160deg, #005a3e 0%, #004D35 50%, #003d2a 100%)", position: "relative", overflow: "hidden", padding: isMobile ? "24px 20px 20px" : "32px 48px 28px" }}>
            <ParticleCanvas />
            <div style={{ position: "relative", zIndex: 2, maxWidth: 700, margin: "0 auto" }}>
              <h1 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 400, color: "#fff", margin: "0 0 8px", lineHeight: 1.15, fontFamily: "Georgia, serif" }}>
                Your <em style={{ color: "#a8f0c6", fontStyle: "italic" }}>recommended</em> advisors
              </h1>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", fontFamily: "sans-serif", margin: "0 0 14px" }}>
                Based on what you told us: {arrivalSummary}
              </p>
              <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "1.5px solid rgba(255,255,255,0.35)", color: "rgba(255,255,255,0.8)", borderRadius: 999, padding: "6px 16px", fontSize: 12, fontFamily: "sans-serif", fontWeight: 600, textDecoration: "none", cursor: "pointer" }}>
                ✎ Change my answers
              </a>
            </div>
          </div>
        ) : (
          <div style={{ background: "linear-gradient(160deg, #005a3e 0%, #004D35 50%, #003d2a 100%)", position: "relative", overflow: "hidden", padding: isMobile ? "32px 20px 28px" : "40px 48px 36px" }}>
            <ParticleCanvas />
            <div style={{ position: "relative", zIndex: 2, maxWidth: 700, margin: "0 auto", textAlign: "center" }}>
              <p style={{ fontSize: 10, fontFamily: "sans-serif", letterSpacing: "0.18em", color: "#a8f0c6", fontWeight: 700, textTransform: "uppercase", marginBottom: 10 }}>◆ BROWSE ALL ADVISORS</p>
              <h1 style={{ fontSize: isMobile ? 26 : "clamp(24px, 2.8vw, 38px)", fontWeight: 400, color: "#fff", margin: "0 0 8px", lineHeight: 1.15, fontFamily: "Georgia, serif" }}>Browse Verified Advisors</h1>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.5)", fontFamily: "sans-serif", margin: 0 }}>
                 {loading ? "Loading advisors..." : totalCount == null ? `${allAdvisors.length} advisors on this page` : `${totalCount} verified advisors`}
              </p>
            </div>
          </div>
        )
      )}

      <div style={{ background: "#fff", borderBottom: "1px solid #e8e5e0", position: "sticky", top: embedded ? 60 : isMobile ? 54 : 58, zIndex: 40 }}>
        <div style={{ padding: isMobile ? "10px 16px" : "10px 48px", overflowX: "auto", display: "flex", gap: 6, alignItems: "center", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
          {ADVISOR_TYPES.map(t => (
            <button key={t} onClick={() => setActiveType(t)} style={{ padding: "6px 14px", borderRadius: 999, border: "1.5px solid", borderColor: activeType === t ? "#004D35" : "#e0ddd8", background: activeType === t ? "#004D35" : "#fff", color: activeType === t ? "#fff" : "#444", fontSize: 13, fontFamily: "sans-serif", fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap", flexShrink: 0 }}>{t}</button>
          ))}
          <div style={{ width: 1, background: "#e8e5e0", alignSelf: "stretch", flexShrink: 0, margin: "0 4px" }} />
          <select
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
            style={{ padding: "6px 12px", borderRadius: 999, border: "1.5px solid", borderColor: stateFilter ? "#004D35" : "#e0ddd8", background: stateFilter ? "rgba(0,77,53,0.06)" : "#fff", color: stateFilter ? "#004D35" : "#555", fontSize: 13, fontFamily: "sans-serif", fontWeight: stateFilter ? 600 : 500, cursor: "pointer", outline: "none", flexShrink: 0, appearance: "none", WebkitAppearance: "none", paddingRight: 28, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23888' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: "no-repeat", backgroundPosition: "right 10px center" }}
          >
            <option value="">All states</option>
            {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {hasFilters && (
            <button onClick={clearAll} style={{ padding: "6px 14px", borderRadius: 999, border: "1.5px solid #e0ddd8", background: "#fff", color: "#888", fontSize: 13, fontFamily: "sans-serif", fontWeight: 500, cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap", flexShrink: 0 }}>
              Clear all
            </button>
          )}
          <button onClick={() => setShowFilterPanel(true)} style={{ padding: "6px 14px", borderRadius: 999, border: "1.5px solid", borderColor: filterCount > 0 ? "#004D35" : "#e0ddd8", background: filterCount > 0 ? "rgba(0,77,53,0.06)" : "#fff", color: filterCount > 0 ? "#004D35" : "#555", fontSize: 13, fontFamily: "sans-serif", fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease", whiteSpace: "nowrap", flexShrink: 0, display: "flex", alignItems: "center", gap: 6 }}>
            ⊞ Filters
            {filterCount > 0 && <span style={{ background: "#22c55e", color: "#fff", borderRadius: "50%", width: 18, height: 18, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}>{filterCount}</span>}
          </button>
        </div>
      </div>

      {(activeType !== "All" || activeSize || stageFilter || (remoteFilter && remoteFilter !== "either") || availabilityFilter || stateFilter) && (
        <div style={{ padding: isMobile ? "8px 16px" : "8px 48px", display: "flex", gap: 6, flexWrap: "wrap", background: "#fff", borderBottom: "1px solid #f0ede8" }}>
          {activeType !== "All" && <FilterTag label={activeType} onRemove={() => setActiveType("All")} />}
          {activeSize && <FilterTag label={activeSize} onRemove={() => setActiveSize("")} />}
          {stageFilter && <FilterTag label={BUSINESS_STAGES.find(s => s.value === stageFilter)?.label || stageFilter} onRemove={() => setStageFilter("")} />}
          {remoteFilter && remoteFilter !== "either" && <FilterTag label={remoteFilter === "remote_friendly" ? "Remote friendly" : "Local only"} onRemove={() => setRemoteFilter("")} />}
          {availabilityFilter && <FilterTag label={availabilityFilter.replace(/_/g, " ")} onRemove={() => setAvailabilityFilter("")} />}
          {stateFilter && <FilterTag label={stateFilter} onRemove={() => setStateFilter("")} />}
        </div>
      )}

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMobile ? "24px 16px" : "36px 48px" }}>
        <label style={{ display: 'block', maxWidth: 560, marginBottom: 22, color: '#44564b', fontSize: 12, fontFamily: 'sans-serif', fontWeight: 700 }}>
          Search advisors and services
          <input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Name, specialty, or service" aria-label="Search advisor names and services" style={{ display: 'block', boxSizing: 'border-box', width: '100%', marginTop: 7, padding: '12px 15px', border: '1.5px solid #d8dfd8', borderRadius: 12, background: '#fff', color: '#173127', fontSize: 14, outlineColor: '#004D35' }} />
        </label>
        {filterResult.relaxedMessage && (
          <div style={{ background: "#fffbeb", border: "1px solid #f5d97d", borderRadius: 12, padding: "12px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 16 }}>🔄</span>
            <span style={{ fontSize: 13, fontFamily: "sans-serif", color: "#7a5a00" }}>{filterResult.relaxedMessage}</span>
            <button onClick={clearAll} style={{ marginLeft: "auto", fontSize: 12, fontFamily: "sans-serif", color: "#7a5a00", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", flexShrink: 0 }}>Clear filters</button>
          </div>
        )}
        {searchError ? (
          <div role="alert" style={{ borderRadius: 14, background: '#fff', border: '1px solid #e4c9c2', padding: 26, color: '#7d3428' }}>
            <p style={{ margin: '0 0 14px' }}>{searchError}</p>
            <button onClick={() => setRetryCount(value => value + 1)} style={{ border: 0, borderRadius: 999, background: '#004D35', color: '#fff', padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}>Retry search</button>
          </div>
        ) : loading ? (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} style={{ background: "#fff", borderRadius: 16, height: 220, border: "1.5px solid #e8e5e0", animation: "pulse 1.5s ease-in-out infinite" }} />
            ))}
          </div>
        ) : filterResult.items.length === 0 ? (
          <div style={{ textAlign: "center", padding: "80px 24px" }}>
            <p style={{ fontSize: 32, marginBottom: 12 }}>🔍</p>
            <h3 style={{ fontSize: 20, fontWeight: 600, color: "#0a1a0f", fontFamily: "Georgia, serif", margin: "0 0 8px" }}>No advisors found</h3>
            <p style={{ fontSize: 14, color: "#aaa", fontFamily: "sans-serif", margin: "0 0 20px" }}>Try adjusting your filters or search terms.</p>
            <button onClick={clearAll} style={{ background: "#004D35", color: "#fff", border: "none", borderRadius: 999, padding: "11px 24px", fontSize: 14, fontFamily: "sans-serif", fontWeight: 600, cursor: "pointer" }}>Clear all filters</button>
          </div>
        ) : view === "list" && !isMobile ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {filterResult.items.map((a, idx) => <AdvisorCard key={a.id} advisor={a} view="list" isMobile={false} isBestMatch={hasFilters && idx === 0} onOpenModal={(adv) => { fetch(getApiUrl(`/api/advisor/${adv.id}/view`), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source: 'card' }) }).catch(() => {}); setProfileModal(adv); }} />)}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
            {filterResult.items.map((a, idx) => <AdvisorCard key={a.id} advisor={a} view="grid" isMobile={isMobile} isBestMatch={hasFilters && idx === 0} onOpenModal={(adv) => { fetch(getApiUrl(`/api/advisor/${adv.id}/view`), { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ source: 'card' }) }).catch(() => {}); setProfileModal(adv); }} />)}
          </div>
        )}
        {!loading && pages > 1 && <nav aria-label="Advisor results pages" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 14, marginTop: 30 }}>
          <button type="button" disabled={page <= 1} onClick={() => { setPage(value => Math.max(1, value - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ padding: '9px 16px', borderRadius: 999, border: '1px solid #d8dfd8', background: '#fff', color: '#004D35', fontWeight: 700, cursor: page <= 1 ? 'not-allowed' : 'pointer', opacity: page <= 1 ? .5 : 1 }}>Previous</button>
          <span aria-live="polite" style={{ color: '#627168', fontSize: 13 }}>Page {page} of {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => { setPage(value => Math.min(pages, value + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ padding: '9px 16px', borderRadius: 999, border: 0, background: '#004D35', color: '#fff', fontWeight: 700, cursor: page >= pages ? 'not-allowed' : 'pointer', opacity: page >= pages ? .5 : 1 }}>Next</button>
        </nav>}
      </div>
    </div>
  );
}
